import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { toast } from 'react-toastify';
import { LuEye, LuEyeOff, LuHouse, LuKeyRound, LuLoaderCircle, LuPhoneCall, LuSave, LuStar } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { Field, TextArea, TextInput } from '../../../components/form/FormControls';
import { auth, db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { PRIMARY_ADDRESS_HOME, PRIMARY_ADDRESS_WORK, getPrimaryAddressKey } from '../../../utils/employeeAddress';
import { HelpNote } from './ProfileUi';

/** The signed-in user's Employees document reference. */
const findOwnEmployeeRef = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  const querySnapshot = await getDocs(query(collection(db, 'Employees'), where('eMail', '==', user.email)));
  return querySnapshot.empty ? null : doc(db, 'Employees', querySnapshot.docs[0].id);
};

const dialogIcon = (Icon, gradient) => (
  <GlassIcon gradient={gradient} size="xs">
    <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
  </GlassIcon>
);

const SaveFooter = ({ onCancel, onSave, saving, disabled, saveLabel }) => {
  const { t } = useTranslation();
  return (
    <>
      <Button variant="ghost" onClick={onCancel} disabled={saving} className="max-sm:tw-hidden">{t('profilePage.common.cancel')}</Button>
      <Button variant="primary" onClick={onSave} disabled={saving || disabled} className="max-sm:tw-flex-1">
        {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
        {saving ? t('profilePage.common.saving') : (saveLabel || t('profilePage.common.save'))}
      </Button>
    </>
  );
};

SaveFooter.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  disabled: PropTypes.bool,
  saveLabel: PropTypes.string,
};

/**
 * Home and work address, and which one is the main address (shown first in
 * Çalışan Yönetimi and on the employee card). The home address is required.
 */
export const AddressDialog = ({ isOpen, userData, onClose }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.dialogs.address.${key}`, vars);
  const [editingAddress, setEditingAddress] = useState('');
  const [editingWorkAddress, setEditingWorkAddress] = useState('');
  const [editingPrimaryAddress, setEditingPrimaryAddress] = useState(PRIMARY_ADDRESS_HOME);
  const [addressLoading, setAddressLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEditingAddress(userData.address || '');
    setEditingWorkAddress(userData.workAddress || '');
    setEditingPrimaryAddress(getPrimaryAddressKey(userData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAddressUpdate = async () => {
    if (!editingAddress.trim()) {
      toast.error(label('homeRequired'));
      return;
    }
    setAddressLoading(true);
    try {
      const docRef = await findOwnEmployeeRef();
      if (!docRef) {
        toast.error(t('profilePage.dialogs.userNotFound'));
        return;
      }
      const trimmedWorkAddress = editingWorkAddress.trim();
      // İş adresi silinmişse ana adres ev adresine döner
      const primaryAddress = trimmedWorkAddress ? editingPrimaryAddress : PRIMARY_ADDRESS_HOME;
      await updateDoc(docRef, {
        address: editingAddress.trim(),
        workAddress: trimmedWorkAddress,
        primaryAddress,
      });
      toast.success(label('saved'));
      onClose();
    } catch (error) {
      console.error('Adres güncelleme hatası:', error);
      toast.error(label('saveError'));
    } finally {
      setAddressLoading(false);
    }
  };

  const hasWork = Boolean(editingWorkAddress.trim());
  const choices = [
    { key: PRIMARY_ADDRESS_HOME, title: label('home'), enabled: true },
    { key: PRIMARY_ADDRESS_WORK, title: label('work'), enabled: hasWork },
  ];
  const primary = hasWork ? editingPrimaryAddress : PRIMARY_ADDRESS_HOME;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !addressLoading && onClose()}
      size="md"
      title={label('title')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={dialogIcon(LuHouse, 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30')}
      footer={<SaveFooter onCancel={onClose} onSave={handleAddressUpdate} saving={addressLoading} disabled={!editingAddress.trim()} />}
    >
      <div className="tw-space-y-4">
        <Field label={label('home')} htmlFor="profile-home-address" required>
          <TextArea id="profile-home-address" rows={3} value={editingAddress} onChange={(event) => setEditingAddress(event.target.value)} placeholder={label('homePlaceholder')} disabled={addressLoading} />
        </Field>
        <Field label={label('work')} htmlFor="profile-work-address" hint={label('workHint')}>
          <TextArea id="profile-work-address" rows={3} value={editingWorkAddress} onChange={(event) => setEditingWorkAddress(event.target.value)} placeholder={label('workPlaceholder')} disabled={addressLoading} />
        </Field>
        <fieldset className="tw-space-y-2">
          <legend className="tw-mb-1.5 tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('primary')}</legend>
          <div role="radiogroup" aria-label={label('primary')} className="tw-grid tw-grid-cols-2 tw-gap-2">
            {choices.map((choice) => {
              const selected = primary === choice.key;
              return (
                <button
                  key={choice.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={!choice.enabled || addressLoading}
                  onClick={() => setEditingPrimaryAddress(choice.key)}
                  className={`tw-flex tw-h-11 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-text-sm tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ${
                    selected
                      ? 'tw-border-amber-400/60 tw-bg-amber-400/15 tw-text-amber-900 dark:tw-text-amber-100'
                      : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-300'
                  }`}
                >
                  <LuStar aria-hidden="true" className={`tw-h-4 tw-w-4 ${selected ? 'tw-fill-amber-400 tw-text-amber-500' : ''}`} />
                  {choice.title}
                </button>
              );
            })}
          </div>
          <p className="tw-px-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{hasWork ? label('primaryHint') : label('primaryHomeOnly')}</p>
        </fieldset>
      </div>
    </Modal>
  );
};

AddressDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  userData: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

/** The emergency contact number (the only phone the user edits here). */
export const EmergencyPhoneDialog = ({ isOpen, userData, onClose, onSaved }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.dialogs.phone.${key}`, vars);
  const [editingEmergencyPhone, setEditingEmergencyPhone] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);

  useEffect(() => {
    if (isOpen) setEditingEmergencyPhone(userData.emergencyPhone || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handlePhoneUpdate = async () => {
    setPhoneLoading(true);
    try {
      const docRef = await findOwnEmployeeRef();
      if (!docRef) {
        toast.error(t('profilePage.dialogs.userNotFound'));
        return;
      }
      await updateDoc(docRef, { emergencyPhone: editingEmergencyPhone.trim() });
      toast.success(label('saved'));
      onSaved(editingEmergencyPhone.trim());
      onClose();
    } catch (error) {
      console.error('Telefon güncelleme hatası:', error);
      toast.error(label('saveError'));
    } finally {
      setPhoneLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !phoneLoading && onClose()}
      size="md"
      title={label('title')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={dialogIcon(LuPhoneCall, 'tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30')}
      footer={<SaveFooter onCancel={onClose} onSave={handlePhoneUpdate} saving={phoneLoading} disabled={!editingEmergencyPhone.trim()} />}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (editingEmergencyPhone.trim()) handlePhoneUpdate();
        }}
      >
        <Field label={label('number')} htmlFor="profile-emergency-phone" hint={label('hint')}>
          <TextInput
            id="profile-emergency-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={editingEmergencyPhone}
            onChange={(event) => setEditingEmergencyPhone(event.target.value)}
            placeholder={label('placeholder')}
            disabled={phoneLoading}
          />
        </Field>
      </form>
    </Modal>
  );
};

EmergencyPhoneDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  userData: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

const PasswordField = ({ id, label, value, onChange, autoComplete, visible, onToggle }) => {
  const { t } = useTranslation();
  return (
    <Field label={label} htmlFor={id}>
      <div className="tw-relative">
        <TextInput id={id} type={visible ? 'text' : 'password'} value={value} onChange={(event) => onChange(event.target.value)} autoComplete={autoComplete} className="tw-pr-11" />
        <button
          type="button"
          onClick={onToggle}
          aria-label={visible ? t('profilePage.dialogs.password.hide') : t('profilePage.dialogs.password.show')}
          aria-pressed={visible}
          className="tw-absolute tw-right-1.5 tw-top-1/2 tw-grid tw-h-8 tw-w-8 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
        >
          {visible ? <LuEyeOff aria-hidden="true" className="tw-h-4 tw-w-4" /> : <LuEye aria-hidden="true" className="tw-h-4 tw-w-4" />}
        </button>
      </div>
    </Field>
  );
};

PasswordField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  autoComplete: PropTypes.string,
  visible: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

/** "Şifremi Değiştir": the current password confirms it's the user. */
export const PasswordDialog = ({ isOpen, onClose }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.dialogs.password.${key}`, vars);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setVisible(false);
  }, [isOpen]);

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(label('errors.required'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(label('errors.mismatch'));
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError(label('errors.length'));
      return;
    }
    setSaving(true);
    try {
      const user = auth.currentUser;
      // Firebase şifre değişikliği için yakın zamanda giriş ister
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, currentPassword));
      await updatePassword(user, newPassword);
      toast.success(label('saved'));
      onClose();
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError(error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential'
        ? label('errors.wrong')
        : label('errors.failed'));
    } finally {
      setSaving(false);
    }
  };

  const strong = newPassword.length >= 6;
  const matches = Boolean(confirmPassword) && newPassword === confirmPassword;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      size="md"
      title={label('title')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={dialogIcon(LuKeyRound, 'tw-from-slate-500 tw-to-slate-700 tw-shadow-slate-500/30')}
      footer={<SaveFooter onCancel={onClose} onSave={handleChangePassword} saving={saving} saveLabel={label('submit')} />}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleChangePassword();
        }}
        className="tw-space-y-4"
      >
        <PasswordField id="profile-current-password" label={label('current')} value={currentPassword} onChange={setCurrentPassword} autoComplete="current-password" visible={visible} onToggle={() => setVisible(!visible)} />
        <PasswordField id="profile-new-password" label={label('new')} value={newPassword} onChange={setNewPassword} autoComplete="new-password" visible={visible} onToggle={() => setVisible(!visible)} />
        <PasswordField id="profile-confirm-password" label={label('confirm')} value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" visible={visible} onToggle={() => setVisible(!visible)} />
        <ul className="tw-space-y-1 tw-text-xs">
          <li className={strong ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-slate-500 dark:tw-text-slate-400'}>{strong ? '✓' : '•'} {label('ruleLength')}</li>
          <li className={matches ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-slate-500 dark:tw-text-slate-400'}>{matches ? '✓' : '•'} {label('ruleMatch')}</li>
        </ul>
        {passwordError && <HelpNote tone="danger">{passwordError}</HelpNote>}
        {/* Enter ile gönderilebilsin */}
        <button type="submit" className="tw-hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
};

PasswordDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
